import React from 'react';
import { positionMenuByTrigger } from '../../utils/menuPosition';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Dropdown } from 'react-bootstrap';
import {
    setFiles,
    setMetadataComparisons,
    upsertMetadataComparisons,
    setPendingComparisons,
    clearPendingComparisons,
    setIsProcessing,
    setProgress,
    resetFilePath,
    setCancelRequested,
    setMetadataAutoAnalyze,
    applyWrittenTags,
    applyFileRename,
    setWriteMode,
    setBeautify,
    toggleMetadataField,
    setDateFormat,
    setFilenameMode,
} from '../../redux/slices/fixMetadataSlice';
import { useDesignV2 } from '../../utils/designV2';
import ToolHeader, { PopoverRow } from '../v2/ToolHeader';
import ToggleSwitch from '../ToggleSwitch';
import CustomSelect from '../CustomSelect';
import { useToast } from '../../contexts/ToastContext';
import { track } from '../../utils/analytics';
import { loadCloudPlaylistFiles } from '../../utils/cloudPlaylistTracks';
import { refreshAndRetry } from '../../redux/utils/refreshAndRetry';
import { formatPath } from "../../utils/formatPath";
import { waitRemaining } from '../../utils/minVisibleDelay';
import { confirmLargeSet, countAudioFiles, effectiveParallelism, SET_COST } from '../../utils/largeSetGuard';
import FixMetadataSettings from "../FixMetadataSettings";
import FixFilenameSettings from "../FixFilenameSettings";
import ProgressBarWithStop from "../ProgressBarWithStop.js";
import MetadataComparison from "../MetadataComparison";
import FilenameComparison from "../FilenameComparison";
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh.svg';
import { ReactComponent as DirectoryIcon } from '../../assets/icons/select-folder.svg';
import { ReactComponent as AnalyzeFilesIcon } from '../../assets/icons/analyze-files.svg';
import { ReactComponent as EditFilenamesIcon } from '../../assets/icons/edit-filenames.svg';
import { ReactComponent as OpenIcon } from '../../assets/icons/open.svg';
import { supportedFormats } from "../../utils/supportedFileFormats";
import { useTierGate } from '../../hooks/useTierGate';
import './SmartRecsTab.scss';

// How often streamed analysis rows are folded into the table (see
// updateProgressFromBackend in handleAnalyze).
const ANALYSIS_FLUSH_MS = 250;

const FixMetadataTab = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { canAccess, requireUpgrade } = useTierGate('metadata-repair');
    const [isDragging, setIsDragging] = React.useState(false);
    const [activeSubTab, setActiveSubTab] = React.useState('tags');
    // DESIGN V2 — structural changes (tool header, tabs in the header,
    // settings popover) are gated on this; the old structure is untouched.
    const v2 = useDesignV2();
    const v2AutoArmedRef = React.useRef(null);  // DESIGN V2: one auto-analyze per loaded source
    const [filenameSelectionCount, setFilenameSelectionCount] = React.useState(0);
    const filenameCompRef = React.useRef(null);

    // Playlist dropdown state
    const [selectedPlaylistName, setSelectedPlaylistName] = React.useState(null);
    const [playlistDropdownOpen, setPlaylistDropdownOpen] = React.useState(false);
    const [playlistList, setPlaylistList] = React.useState([]);
    const [playlistsLoading, setPlaylistsLoading] = React.useState(false);
    const [playlistSearch, setPlaylistSearch] = React.useState('');
    const [playlistDropdownPos, setPlaylistDropdownPos] = React.useState({ top: 0, left: 0, width: 250 });
    const playlistDropdownRef = React.useRef(null);
    const playlistBtnRef = React.useRef(null);
    // Flip the "Open ▸ Playlist" picker above its button when there's no room below.
    React.useLayoutEffect(() => {
        if (!playlistDropdownOpen || !playlistDropdownRef.current || !playlistBtnRef.current) return;
        const rect = playlistBtnRef.current.getBoundingClientRect();
        const { top } = positionMenuByTrigger(playlistDropdownRef.current, rect, { gap: 2, align: null });
        setPlaylistDropdownPos(prev => (prev.top === top ? prev : { ...prev, top }));
    }, [playlistDropdownOpen, playlistList, playlistSearch, playlistsLoading]);
    const playlistSearchRef = React.useRef(null);

    // Selectors
    const {
        files,
        metadataComparisons,
        isProcessing,
        progress,
        cancelRequested,
        backupBeforeWrite,
        writeMode,
        beautify,
        enabledFields,
        filenameMode,
        autoAnalyze,
        dateFormat
    } = useSelector((state) => state.fixMetadata);

    // The single "Existing Tags" dropdown maps to the two backend flags:
    //   fillEmpty     → touch nothing existing
    //   overwriteText → replace existing text, protect keys
    //   overwriteAll  → replace everything, keys included
    const overwriteExisting = writeMode === 'overwriteText' || writeMode === 'overwriteAll';
    const overrideExistingKey = writeMode === 'overwriteAll';

    const {
        enableRecursive,
        concurrencyLevel,
    } = useSelector((state) => state.settings);

    // Signed-in users' playlists live in the cloud — the picker lists those
    // and resolves their tracks to local files (utils/cloudPlaylistTracks).
    const accessToken = useSelector((state) => state.auth.accessToken);
    const useServer = !!accessToken;

    // Source identity guard (same as FixMyTracksTab): playlist selection
    // resolves ASYNCHRONOUSLY, and an Analyze click racing it would run the
    // PREVIOUS source while the header already showed the playlist.
    const sourceEpochRef = React.useRef(0);
    const [sourceResolving, setSourceResolving] = React.useState(false);

    // Any change to the source value bumps the epoch — including dispatches
    // from OUTSIDE this tab (playlist sidebar "Open in tool"), which the
    // per-handler bumps can't see.
    const sourceFiles_watch = React.useRef(files);
    React.useEffect(() => {
        if (sourceFiles_watch.current !== files) {
            sourceFiles_watch.current = files;
            sourceEpochRef.current += 1;
        }
    }, [files]);

    // Handlers
    const handleChooseFiles = async () => {
        setSelectedPlaylistName(null);
        const chosenFiles = await window.electron.openFile(supportedFormats);
        sourceEpochRef.current += 1;
        dispatch(setFiles(chosenFiles || []));
    };

    const handleChooseDir = async () => {
        const chosenDir = await window.electron.openDir();
        if (!chosenDir || chosenDir.length === 0) return; // dialog canceled — keep current selection
        setSelectedPlaylistName(null);
        sourceEpochRef.current += 1;
        dispatch(setFiles(chosenDir));
    };

    const handleChooseLibrary = async () => {
        try {
            const allTracks = await window.electron.db.tracks.getAll();
            const filePaths = (allTracks || [])
                .map(t => t.file_path || t.filePath || t.file)
                .filter(f => f && !f.startsWith('online://') && !f.startsWith('http://') && !f.startsWith('https://'));
            if (filePaths.length === 0) {
                toast.warning('Your library is empty'); return;
            }
            setSelectedPlaylistName('My Library');
            sourceEpochRef.current += 1;
            dispatch(setFiles(filePaths));
        } catch (error) {
            console.error('Error loading library:', error);
            toast.error('Failed to load library');
        }
    };

    const handleResetFilePath = () => {
        sourceEpochRef.current += 1;
        dispatch(resetFilePath());
    };

    // Close playlist dropdown on outside click
    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (playlistDropdownRef.current && !playlistDropdownRef.current.contains(e.target)) {
                setPlaylistDropdownOpen(false);
            }
        };
        if (playlistDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [playlistDropdownOpen]);

    const handlePlaylistDropdownToggle = async (anchorEl) => {
        if (playlistDropdownOpen) { setPlaylistDropdownOpen(false); return; }
        setPlaylistSearch('');
        if (anchorEl) {
            const rect = anchorEl.getBoundingClientRect();
            setPlaylistDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 250) });
        }
        setPlaylistDropdownOpen(true);
        setTimeout(() => playlistSearchRef.current?.focus(), 50);
        setPlaylistsLoading(true);
        try {
            if (useServer) {
                const result = await refreshAndRetry((token) => window.electron.library.getPlaylists(token));
                setPlaylistList(result?.data || []);
            } else {
                const allPlaylists = await window.electron.db.playlists.getAll(false);
                setPlaylistList(allPlaylists || []);
            }
        } catch (error) {
            console.error('Error loading playlists:', error);
            toast.error('Failed to load playlists');
        } finally {
            setPlaylistsLoading(false);
        }
    };

    const handlePlaylistSelect = async (playlist) => {
        setPlaylistDropdownOpen(false);
        setSourceResolving(true);
        try {
            let filePaths = [];
            let total = 0;
            if (useServer) {
                // Cloud playlist — resolve rows to local files (exact link /
                // origin path, one-time heuristic backfill for legacy rows).
                const loaded = await loadCloudPlaylistFiles(accessToken, playlist.id);
                if (!loaded || loaded.total === 0) {
                    toast.warning('This playlist has no tracks'); return;
                }
                total = loaded.total;
                filePaths = loaded.filePaths;
                if (filePaths.length === 0) {
                    toast.warning('None of this playlist’s tracks are on this device — these tools work on local audio files');
                    return;
                }
            } else {
                const playlistData = await window.electron.db.playlists.get(playlist.id, true);
                if (!playlistData || !playlistData.tracks || playlistData.tracks.length === 0) {
                    toast.warning('This playlist has no tracks'); return;
                }
                const tracks = playlistData.tracks.filter(t => t.file || t.filePath);
                if (tracks.length === 0) { toast.warning('No valid file paths found in playlist'); return; }
                // Filter out cloud/online tracks — they don't have local files
                total = tracks.length;
                filePaths = tracks
                    .map(t => t.file || t.filePath)
                    .filter(f => !f.startsWith('online://') && !f.startsWith('http://') && !f.startsWith('https://'));
                if (filePaths.length === 0) {
                    toast.warning('This playlist only contains cloud tracks — no local files to fix');
                    return;
                }
            }
            setSelectedPlaylistName(playlist.name);
            sourceEpochRef.current += 1;
            dispatch(setFiles(filePaths));
            const skipped = total - filePaths.length;
            if (skipped > 0) toast.info(`Skipped ${skipped} ${skipped === 1 ? 'track' : 'tracks'} with no local file on this device`);
            track('playlist_opened_in_tool', {
                tab: 'fix-metadata',
                source: useServer ? 'cloud' : 'local',
                track_count: filePaths.length,
                skipped_count: skipped,
            });
        } catch (error) {
            console.error('Error loading playlist tracks:', error);
            toast.error('Failed to load playlist tracks');
        } finally {
            setSourceResolving(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFiles = Array.from(e.dataTransfer.files).map(file => window.electron.getPathForFile(file)).filter(Boolean);
        if (droppedFiles.length > 0) {
            sourceEpochRef.current += 1;
            dispatch(setFiles(droppedFiles));
        }
    };

    const handleRefresh = () => {
        handleAnalyze(); // Run analyze again
    };

    const handleStopProcessing = () => {
        dispatch(setCancelRequested(true));
        dispatch(setIsProcessing(false));
        dispatch(clearPendingComparisons()); // Cancelled rows must not linger as "Queued"
        window.electron.cancelFixMetadata();
    };

    const handleAnalyze = async () => {
        // Source captured at click time; abort if it changes during any await
        // below (see sourceEpochRef).
        const runEpoch = sourceEpochRef.current;
        const sourceChanged = () => sourceEpochRef.current !== runEpoch;
        // Time-based large-selection gate (silent for small sets) — applies to
        // library, playlist, folder and dropped files alike.
        const fileCount = await countAudioFiles(files);
        const sourceLabel = selectedPlaylistName === 'My Library' ? 'library'
            : selectedPlaylistName ? 'playlist'
            : (files.length === 1 && fileCount !== 1) ? 'folder' : 'selection';
        if (!(await confirmLargeSet(toast, fileCount, {
            secondsPerFile: SET_COST.fixMetadata,
            parallelism: effectiveParallelism(),
            sourceLabel,
            tab: 'fix_metadata',
        }))) return;
        if (sourceChanged()) return;

        window.electron.analytics.track('metadata_analyze');
        dispatch(setIsProcessing(true));
        dispatch(setProgress(0));
        dispatch(setMetadataComparisons([]));

        const startTime = Date.now(); // Record the start time

        // Per-file ticks carry just the row that finished (`comparison`);
        // the final tick carries the whole settled list. Rows are batched and
        // flushed a few times a second — with 6 files finishing at once, a
        // dispatch per row meant a full table re-render per file, which is
        // what made a 200-file run stall. The final tick supersedes anything
        // still pending.
        let pendingRows = [];
        let pendingProgress = null;
        let flushTimer = null;
        const flush = () => {
            flushTimer = null;
            if (pendingRows.length) {
                dispatch(upsertMetadataComparisons(pendingRows));
                pendingRows = [];
            }
            if (pendingProgress != null) {
                dispatch(setProgress(pendingProgress));
                pendingProgress = null;
            }
        };
        const updateProgressFromBackend = async ({ progressPercentage, comparisons, comparison, pending }) => {
            // The analyzer's first tick: every file it is about to walk. Lay
            // them all out as placeholder rows so the table takes its final
            // shape now and results fill in place (as Compatibility does).
            if (pending) {
                dispatch(setPendingComparisons(pending));
                return;
            }
            if (progressPercentage >= 100) {
                if (flushTimer) clearTimeout(flushTimer);
                flushTimer = null;
                pendingRows = [];
                pendingProgress = null;
                await waitRemaining(startTime); // Keep progress visible briefly so fast runs don't flash-complete (see minVisibleDelay)
                dispatch(setProgress(progressPercentage));
                if (comparisons && comparisons.length > 0) {
                    dispatch(setMetadataComparisons(comparisons));
                } else {
                    // Stopped / failed before any row finished: the placeholders
                    // must not linger as if they were results.
                    dispatch(clearPendingComparisons());
                }
                dispatch(setIsProcessing(false));
                return;
            }
            if (comparison) pendingRows.push(comparison);
            else if (comparisons && comparisons.length > 0) pendingRows.push(...comparisons);
            pendingProgress = progressPercentage;
            if (!flushTimer) flushTimer = setTimeout(flush, ANALYSIS_FLUSH_MS);
        };

        // Check if the single item is likely a directory (no file extension) or a file
        const isSingleDirectory = files.length === 1 && !files[0].match(/\.\w{2,5}$/);

        const options = {
            dir: isSingleDirectory ? files[0] : null,
            files: isSingleDirectory ? null : files,
            includeSubDirs: enableRecursive,
            overwriteExisting,
            overrideExistingKey,
            beautify,
            enabledFields,
            filenameMode,
            // Lets analysis treat a date already written in this display format as
            // "no change" instead of re-flagging it against the raw API date.
            dateFormat,
            // Needed for the Mixpedia search (same endpoint/auth the enricher uses).
            djpalToken: localStorage.getItem('djpal:token') || ''
        };

        window.electron.analyzeMetadata(
            options,
            updateProgressFromBackend
        );
    };

    // Auto-analyze when triggered from playlist sidebar
    React.useEffect(() => {
        // DESIGN V2: a loaded source analyzes on its own; the button is the write step.
        // Arm once per load: a new files array, or the same array loaded again
        // after its results were cleared (not during a run, which clears too).
        if (v2 && files.length > 0 && metadataComparisons.length === 0 && !isProcessing && !autoAnalyze
            && (v2AutoArmedRef.current?.files !== files || Date.now() - v2AutoArmedRef.current.at > 3000)) {
            v2AutoArmedRef.current = { files: files, at: Date.now() };
            dispatch(setMetadataAutoAnalyze(true));
            return;
        }
        if (autoAnalyze && files.length > 0 && !isProcessing) {
            dispatch(setMetadataAutoAnalyze(false));
            handleAnalyze();
        }
    }, [autoAnalyze, files, metadataComparisons.length, isProcessing]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleWriteTags = async () => {
        const selectedFiles = metadataComparisons.filter(item => item.selected);

        if (selectedFiles.length === 0) {
            return;
        }

        window.electron.analytics.track('metadata_write', { track_count: selectedFiles.length });

        dispatch(setIsProcessing(true));
        dispatch(setProgress(0));

        const startTime = Date.now(); // Record the start time

        const updateProgressFromBackend = async ({ progressPercentage, results, notice }) => {
            // Follow-up DJ-library sync may report an app was open (e.g. "Rekordbox
            // is open — MixGoat will update it once it closes").
            if (notice) toast.info(notice);
            if (progressPercentage >= 100) {
                await waitRemaining(startTime); // Keep progress visible briefly so fast runs don't flash-complete (see minVisibleDelay)
                dispatch(setProgress(progressPercentage));
                dispatch(setIsProcessing(false));
                // Update written files in-place — no full re-analysis
                if (results) {
                    for (const result of results) {
                        if (result.success && result.tagsWritten) {
                            dispatch(applyWrittenTags({ filePath: result.filePath, tagsWritten: result.tagsWritten }));
                        }
                    }
                    // Failed rows stay flagged in the list — say why instead of
                    // looking like the write silently did nothing.
                    const failed = results.filter(r => r && !r.success);
                    if (failed.length > 0) {
                        toast.error(`Failed to write tags to ${failed.length} file${failed.length > 1 ? 's' : ''}`);
                    }
                }
            } else {
                dispatch(setProgress(progressPercentage));
            }
        };

        const options = {
            files: selectedFiles,
            backupBeforeWrite: false,
            overwriteExisting,
            overrideExistingKey,
            concurrencyLevel,
            dateFormat
        };

        window.electron.writeMetadata(
            options,
            updateProgressFromBackend
        );
    };

    const handleRenameFiles = async () => {
        const selectedFiles = filenameCompRef.current?.getSelectedFiles() || [];
        if (selectedFiles.length === 0) return;

        window.electron.analytics.track('metadata_rename_files', { track_count: selectedFiles.length });

        dispatch(setIsProcessing(true));
        dispatch(setProgress(0));

        const startTime = Date.now(); // Record the start time

        let handledComplete = false;
        const updateProgressFromBackend = async ({ progressPercentage, results, notice }) => {
            if (notice) toast.info(notice);
            if (progressPercentage >= 100 && !handledComplete) {
                handledComplete = true;
                await waitRemaining(startTime); // Keep progress visible briefly so fast runs don't flash-complete (see minVisibleDelay)
                dispatch(setProgress(progressPercentage));
                dispatch(setIsProcessing(false));
                if (results) {
                    let renamed = 0;
                    for (const result of results) {
                        if (result.success && result.newPath) {
                            dispatch(applyFileRename({ filePath: result.filePath, newPath: result.newPath, newName: result.newName }));
                            renamed++;
                        }
                    }
                    if (renamed > 0) toast.success(`Renamed ${renamed} file${renamed > 1 ? 's' : ''}`);
                }
            } else if (!handledComplete) {
                dispatch(setProgress(progressPercentage));
            }
        };

        window.electron.renameFiles(
            { files: selectedFiles },
            updateProgressFromBackend
        );
    };

    const selectedCount = metadataComparisons.filter(item => item.selected).length;
    const renameCount = filenameSelectionCount;
    // The action buttons' count ("Write Selected Tags (N)") climbs on every
    // analysis tick, and the row is right-aligned — so a live count pushed
    // Open/Analyze left and squeezed the progress bar on every tick. Same rule
    // as Compatibility / Loudness: no count while a run is in progress, and
    // none for zero; it appears once with the results.
    const hasAnalyzed = metadataComparisons.length > 0;

    // Shared by both designs: the Open menu (same #open-dropdown and
    // .dropdown-item hooks the QA matrix drives) and the playlist picker.
    const sourceLabel = files.length === 0
        ? 'Open'
        : selectedPlaylistName
            ? selectedPlaylistName
            : files.length > 1
                ? `${files.length} files`
                : (files[0].split('/').pop() || files[0]);
    const openMenu = (
        <Dropdown>
            <Dropdown.Toggle
                className={v2 ? 'v2-btn v2-source-chip' : 'button choose-button'}
                id="open-dropdown"
                disabled={isProcessing}
                ref={playlistBtnRef}
                title={v2 ? (files.length === 1 ? files[0] : '') : undefined}
            >
                {v2 ? (
                    <>
                        <DirectoryIcon className="icon" />
                        <span className="v2-source-label">{sourceLabel}</span>
                        {selectedPlaylistName && files.length > 0 && <span className="v2-faint v2-num">· {files.length}</span>}
                    </>
                ) : (
                    <><OpenIcon className="icon" />Open</>
                )}
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item onClick={handleChooseFiles}>Files</Dropdown.Item>
                <Dropdown.Item onClick={handleChooseDir}>Folder</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => handlePlaylistDropdownToggle(playlistBtnRef.current)}>Playlist</Dropdown.Item>
                <Dropdown.Item onClick={handleChooseLibrary}>My Library</Dropdown.Item>
                {v2 && files.length > 0 && (
                    <>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={handleResetFilePath}>Clear</Dropdown.Item>
                    </>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
    const playlistPicker = playlistDropdownOpen && (
        <div ref={playlistDropdownRef} className="smart-recs-dropdown-menu" style={{ position: 'fixed', top: playlistDropdownPos.top, left: playlistDropdownPos.left, width: playlistDropdownPos.width, zIndex: 1000 }}>
            <div className="dropdown-search-wrapper">
                <svg className="dropdown-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input ref={playlistSearchRef} className="dropdown-search-input" type="text" placeholder="Search playlists..." value={playlistSearch} onChange={(e) => setPlaylistSearch(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
            </div>
            <div className="dropdown-options">
                {playlistsLoading ? (
                    <div className="dropdown-skel" aria-label="Loading playlists">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="dropdown-skel-option"><span /></div>)}</div>
                ) : (
                    <>
                        {playlistList.filter(pl => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase())).map((pl) => (
                            <button key={pl.id} className="dropdown-option" onClick={() => handlePlaylistSelect(pl)}>
                                <span className="dropdown-option-name">{pl.name}</span>
                            </button>
                        ))}
                        {playlistList.filter(pl => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase())).length === 0 && (
                            playlistList.length === 0 ? (
                            <div className="dropdown-no-results" style={{ whiteSpace: 'normal', lineHeight: 1.4, textAlign: 'left', padding: '10px 12px' }}>
                                No playlists yet. These tools work on audio files on this computer — import a library (Rekordbox, Serato, Traktor, VirtualDJ) or add local tracks.
                            </div>
                        ) : (
                            <div className="dropdown-no-results">No playlists match your search</div>
                        )
                        )}
                    </>
                )}
            </div>
        </div>
    );

    // ---- DESIGN V2 header ----
    const analyzedCount = metadataComparisons.filter((c) => !c.pending).length;
    const changedCount = metadataComparisons.filter((c) => c.hasChanges).length;
    const mode = filenameMode || 'both';
    const renameSuggestions = mode === 'none' ? 0 : metadataComparisons.filter((c) => !c.pending && (c.filenameByMode ? c.filenameByMode[mode] : c.suggestedFilename)).length;
    // Fields that differ (and are enabled) across the selected rows — what a
    // Write would actually touch.
    const fieldsToWrite = React.useMemo(() => {
        const keys = Object.keys(enabledFields || {}).filter((k) => enabledFields[k]);
        let n = 0;
        for (const c of metadataComparisons) {
            if (c.pending || !c.selected || !c.database) continue;
            for (const k of keys) {
                const cur = String(c.current?.[k] ?? '').trim();
                const db = String(c.database?.[k] ?? '').trim();
                if (db && cur !== db) n++;
            }
        }
        return n;
    }, [metadataComparisons, enabledFields]);
    const prettifyOn = mode === 'prettify' || mode === 'both';
    const dbOn = mode === 'fixFromDb' || mode === 'both';
    const setPrettify = (val) => dispatch(setFilenameMode(val ? (dbOn ? 'both' : 'prettify') : (dbOn ? 'fixFromDb' : 'none')));
    const setUseDb = (val) => dispatch(setFilenameMode(val ? (prettifyOn ? 'both' : 'fixFromDb') : (prettifyOn ? 'prettify' : 'none')));
    const FIELD_CHIPS = [['artist', 'Artist'], ['title', 'Title'], ['album', 'Album'], ['albumArtist', 'Album artist'], ['label', 'Label'], ['releaseDate', 'Release'], ['genre', 'Genre'], ['bpm', 'BPM'], ['key', 'Key']];
    const primaryDisabled = !hasAnalyzed || isProcessing || (activeSubTab === 'tags' ? selectedCount === 0 : renameCount === 0);
    const primaryTitle = !hasAnalyzed ? 'Analyze first' : (activeSubTab === 'tags' && selectedCount === 0) ? 'Select the rows to write' : (activeSubTab === 'filenames' && renameCount === 0) ? 'Nothing to rename' : '';
    const v2Header = v2 && (
        <ToolHeader
            className="v2-tool-metadata"
            source={<>{openMenu}{playlistPicker}</>}
            stats={isProcessing ? (
                <ProgressBarWithStop isProcessing={isProcessing} progress={progress} handleStopProcessing={handleStopProcessing} v2 />
            ) : files.length === 0 ? (
                null /* the empty page below already says it; repeating it here read as a stray line */
            ) : hasAnalyzed ? (
                activeSubTab === 'tags'
                    ? <><b>{analyzedCount}</b> analyzed · <b>{changedCount}</b> with changes · <b>{fieldsToWrite}</b> fields to write</>
                    : <><b>{analyzedCount}</b> analyzed · <b>{renameSuggestions}</b> need renaming</>
            ) : (
                <><b>{files.length}</b> {files.length === 1 && !selectedPlaylistName ? 'item' : 'files'} · not analyzed yet</>
            )}
            settings={activeSubTab === 'tags' ? (
                <>
                    <div className="v2-popover-title">Settings</div>
                    <PopoverRow label="Beautify titles" hint="Title Case, tidy spacing and brackets"><ToggleSwitch id="v2-beautify" checked={!!beautify} onChange={(v) => dispatch(setBeautify(v))} label="" /></PopoverRow>
                    <div className="v2-popover-row"><div>Existing tags</div>
                        <div style={{ width: 170 }}><CustomSelect value={writeMode} onChange={(e) => dispatch(setWriteMode(e.target.value))} className="organizer-select compact-select"
                            options={[{ value: 'fillEmpty', label: 'Only fill empty' }, { value: 'overwriteText', label: 'Overwrite text, keep keys' }, { value: 'overwriteAll', label: 'Overwrite everything' }]} /></div></div>
                    <div className="v2-popover-row"><div>Date format</div>
                        <div style={{ width: 170 }}><CustomSelect value={dateFormat} onChange={(e) => dispatch(setDateFormat(e.target.value))} className="organizer-select compact-select"
                            options={[{ value: 'fullDate', label: 'YYYY-MM-DD' }, { value: 'monthAndYear', label: 'YYYY-MM' }, { value: 'year', label: 'YYYY' }, { value: 'dmyDot', label: 'DD.MM.YYYY' }, { value: 'dmySlash', label: 'DD/MM/YYYY' }, { value: 'mdySlash', label: 'MM/DD/YYYY' }]} /></div></div>
                    <div className="v2-popover-row v2-popover-block">
                        <div>Fields to write</div>
                        <div className="v2-chip-wrap">
                            {FIELD_CHIPS.map(([k, l]) => (
                                <button key={k} type="button" className={`v2-chip sm${enabledFields?.[k] ? ' on' : ''}`} onClick={() => dispatch(toggleMetadataField(k))}>{l}</button>
                            ))}
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="v2-popover-title">Rename options</div>
                    <PopoverRow label="Prettify" hint="Capitalize each word, strip leading track numbers"><ToggleSwitch id="v2-filenamePrettify" checked={prettifyOn} onChange={setPrettify} label="" /></PopoverRow>
                    <PopoverRow label="Use database names" hint="Artist - Title from Mixpedia when found"><ToggleSwitch id="v2-filenameUseDb" checked={dbOn} onChange={setUseDb} label="" /></PopoverRow>
                </>
            )}
            actions={hasAnalyzed ? (
                <>
                    <button type="button" className="v2-btn analyze-button" onClick={handleAnalyze} disabled={!files.length || isProcessing || sourceResolving}>Analyze again</button>
                    <button type="button" className="v2-btn primary" aria-disabled={primaryDisabled ? 'true' : undefined} title={primaryTitle}
                        onClick={() => { if (primaryDisabled) return; if (!canAccess) { requireUpgrade(); return; } if (activeSubTab === 'tags') handleWriteTags(); else handleRenameFiles(); }}>
                        {activeSubTab === 'tags'
                            ? `Write${!isProcessing && selectedCount > 0 ? ` ${selectedCount}` : ''} selected`
                            : `Rename${!isProcessing && renameCount > 0 ? ` ${renameCount}` : ''} selected`}
                    </button>
                </>
            ) : files.length > 0 && !isProcessing ? (
                <button type="button" className="v2-btn primary analyze-button" onClick={handleAnalyze} disabled={!files.length || isProcessing || sourceResolving}
                    title={files.length === 0 ? 'Open files, a folder or a playlist first' : ''}>Analyze</button>
            ) : null}
        />
    );
    // Tags / Filenames sit in the row under the header, like the other tools'
    // filters: in the header they squeezed the summary to "13 analyzed · …".
    const v2TabsRow = v2 && files.length > 0 && (
        <div className="v2-filter-row">
            <div className="v2-tabs" role="tablist">
                <button type="button" role="tab" aria-selected={activeSubTab === 'tags'} className={activeSubTab === 'tags' ? 'on' : ''} onClick={() => setActiveSubTab('tags')}>
                    Tags{changedCount > 0 && <span className="v2-faint v2-num" style={{ marginLeft: 6 }}>{changedCount}</span>}
                </button>
                <button type="button" role="tab" aria-selected={activeSubTab === 'filenames'} className={activeSubTab === 'filenames' ? 'on' : ''} onClick={() => setActiveSubTab('filenames')}>
                    Filenames{renameSuggestions > 0 && <span className="v2-faint v2-num" style={{ marginLeft: 6 }}>{renameSuggestions}</span>}
                </button>
            </div>
        </div>
    );

    return (
        <div
            className={`tab-container ${v2 ? 'v2-tool-page v2-metadata-page ' : ''}${isDragging ? 'dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {v2Header}
            {v2TabsRow}
            {!v2 && <div className="upper-part source-bar-header">
                <div className="source-bar">
                    <div className="source-bar-left">
                        {isProcessing ? (
                            <ProgressBarWithStop
                                isProcessing={isProcessing}
                                progress={progress}
                                handleStopProcessing={handleStopProcessing}
                            />
                        ) : (
                            <>
                                <DirectoryIcon className="source-bar-icon" />
                                <span className={`source-bar-title${files.length === 0 ? ' empty' : ''}`} title={files.length === 1 ? files[0] : ''}>
                                    {files.length === 0
                                        ? 'Click "Open" or drop files here'
                                        : selectedPlaylistName
                                            ? selectedPlaylistName
                                            : files.length > 1
                                                ? `${files.length} Files`
                                                : (files[0].split('/').pop() || files[0])}
                                </span>
                                {selectedPlaylistName && files.length > 0 && <span className="source-bar-meta">· {files.length} files</span>}
                                {files.length > 0 && (
                                    <button className="source-bar-clear" onClick={handleResetFilePath} aria-label="Clear" title="Clear">&times;</button>
                                )}
                            </>
                        )}
                    </div>
                    <div className="source-bar-actions">
                        {hasAnalyzed && !isProcessing && (
                            <button className="source-bar-icon-btn" onClick={handleRefresh} title="Refresh" aria-label="Refresh"><RefreshIcon className="icon" /></button>
                        )}
                        {openMenu}
                        {playlistPicker}
                        <Button
                            className="button analyze-button main-button"
                            onClick={handleAnalyze}
                            disabled={!files.length || isProcessing || sourceResolving}
                        >
                            <AnalyzeFilesIcon className="icon"/>
                            Analyze
                        </Button>
                        {activeSubTab === 'tags' && (
                            <Button
                                className="button main-button"
                                onClick={() => { if (!canAccess) { requireUpgrade(); return; } handleWriteTags(); }}
                                disabled={!hasAnalyzed || selectedCount === 0 || isProcessing}
                            >
                                <EditFilenamesIcon className="icon"/>
                                Write Selected Tags{!isProcessing && selectedCount > 0 ? ` (${selectedCount})` : ''}
                            </Button>
                        )}
                        {activeSubTab === 'filenames' && (
                            <Button
                                className="button main-button"
                                onClick={() => { if (!canAccess) { requireUpgrade(); return; } handleRenameFiles(); }}
                                disabled={!hasAnalyzed || renameCount === 0 || isProcessing}
                            >
                                <EditFilenamesIcon className="icon"/>
                                Rename Selected{!isProcessing && renameCount > 0 ? ` (${renameCount})` : ''}
                            </Button>
                        )}
                    </div>
                </div>
            </div>}
            <div className="lower-part">
                <div className="log-container">
                    <div className="files-container">
                        {files.length === 0 ? (
                            <div className="ftx-empty-state" onClick={handleChooseDir} role="button" tabIndex={0}>
                                <DirectoryIcon className="ftx-empty-icon" />
                                <div className="ftx-empty-title">Drop tracks here to fix metadata</div>
                                <div className="ftx-empty-sub">or click to load files, a folder, or a playlist</div>
                            </div>
                        ) : (<>
                            {activeSubTab === 'tags' && <MetadataComparison activeSubTab={v2 ? null : activeSubTab} onSubTabChange={v2 ? null : setActiveSubTab} />}
                            {activeSubTab === 'filenames' && <FilenameComparison ref={filenameCompRef} activeSubTab={v2 ? null : activeSubTab} onSubTabChange={v2 ? null : setActiveSubTab} onSelectionChange={setFilenameSelectionCount} />}
                        </>)}
                    </div>
                    {!v2 && activeSubTab === 'tags' && <FixMetadataSettings />}
                    {!v2 && activeSubTab === 'filenames' && <FixFilenameSettings />}
                </div>
            </div>
        </div>
    );
};

export default FixMetadataTab;
